import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useMarket } from '../../../contexts/MarketContext';
import { healthCheck } from '../../../services/api';

const pad2 = (n) => String(n).padStart(2, '0');

const formatClock = (date, offsetHours) => {
  const t = new Date(date.getTime() + (offsetHours - (-date.getTimezoneOffset() / 60)) * 3600_000);
  return `${pad2(t.getHours())}:${pad2(t.getMinutes())}:${pad2(t.getSeconds())}`;
};

// Compute trading-session indicator for the current market.
// A-shares: 09:30-11:30 + 13:00-15:00 CST → LIVE; outside → PRE/CLOSED
// Crypto: always LIVE (24/7)
// Futures: 09:00-10:15, 10:30-11:30, 13:30-15:00 day + 21:00-02:30 night
const sessionFor = (marketId, t) => {
  const sh = new Date(t.getTime() + (8 - (-t.getTimezoneOffset() / 60)) * 3600_000);
  const day = sh.getUTCDay(); // 0=Sun, 6=Sat in shanghai time (after offset)
  const minutes = sh.getUTCHours() * 60 + sh.getUTCMinutes();
  if (marketId === 'crypto') return 'live';
  if (day === 0 || day === 6) return 'weekend';
  if (marketId === 'a_stock') {
    if (minutes < 9 * 60 + 15) return 'pre';
    if ((minutes >= 9 * 60 + 30 && minutes <= 11 * 60 + 30) ||
        (minutes >= 13 * 60 && minutes <= 15 * 60)) return 'live';
    if (minutes > 15 * 60) return 'closed';
    return 'pre';
  }
  if (marketId === 'chinese_futures') {
    if ((minutes >= 9 * 60 && minutes <= 10 * 60 + 15) ||
        (minutes >= 10 * 60 + 30 && minutes <= 11 * 60 + 30) ||
        (minutes >= 13 * 60 + 30 && minutes <= 15 * 60)) return 'live';
    if (minutes >= 21 * 60 || minutes <= 2 * 60 + 30) return 'night';
    return 'closed';
  }
  return 'closed';
};

const sessionToKey = (s) => ({
  pre: 'termSessionPre',
  live: 'termSessionLive',
  closed: 'termSessionClosed',
  night: 'termSessionNight',
  weekend: 'termSessionWeekend',
}[s] || 'termSessionClosed');

const StatusBar = () => {
  const { t } = useLanguage();
  const { currentMarket } = useMarket();
  const [now, setNow] = useState(() => new Date());
  const [apiState, setApiState] = useState('unknown'); // ok | warn | err | unknown

  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const probe = async () => {
      try {
        const res = await healthCheck();
        if (cancelled) return;
        if (res?.maintenance) setApiState('warn');
        else setApiState('ok');
      } catch {
        if (!cancelled) setApiState('err');
      }
    };
    probe();
    const id = setInterval(probe, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const user = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  })();
  const username = user?.username || user?.email || '-';

  const sessionKey = sessionToKey(sessionFor(currentMarket.id, now));
  const apiDotClass = apiState === 'ok' ? 'term-dot-ok'
                     : apiState === 'warn' ? 'term-dot-warn'
                     : apiState === 'err' ? 'term-dot-err'
                     : 'term-dot-warn';
  const apiLabelKey = apiState === 'ok' ? 'termStatusOperational'
                     : apiState === 'warn' ? 'termStatusDegraded'
                     : apiState === 'err' ? 'termStatusOffline'
                     : 'termStatusDegraded';

  return (
    <div className="term-statusbar">
      <div className="seg">
        <span className="lbl">{t('termStatusClock')}</span>
        <span className="val term-mono">{formatClock(now, 8)}</span>
      </div>
      <div className="seg">
        <span className="lbl">{t('termStatusUtc')}</span>
        <span className="val term-mono">{formatClock(now, 0)}</span>
      </div>
      <div className="seg">
        <span className="lbl">{t('termStatusSession')}</span>
        <span className="val">{t(sessionKey)}</span>
      </div>
      <div className="seg">
        <span className="lbl">{t('termStatusApi')}</span>
        <span className={`term-dot ${apiDotClass}`} />
        <span className="val">{t(apiLabelKey)}</span>
      </div>
      <div className="seg" style={{ marginLeft: 'auto' }}>
        <span className="lbl">{t('termStatusUser')}</span>
        <span className="val">{username}</span>
      </div>
      <div className="seg">
        <span className="lbl">{t('termStatusBuild')}</span>
        <span className="val">{(process.env.REACT_APP_BUILD_SHA || 'dev').slice(0, 7)}</span>
      </div>
    </div>
  );
};

export default StatusBar;
